import React, { useState } from 'react';
import { Button, Form, ListGroup, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useSelector } from "react-redux";
import Select from 'components/general/ViewportSelect';

const AddPoll = ({ agenda, meetingId, getPolls }) => {
  const { user: authUser } = useSelector(x => x.auth);
  const url = process.env.REACT_APP_URL;

  const [showPollForm, setShowPollForm] = useState(false);
  const [selectedPollEvent, setSelectedPollEvent] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);

  // Get already created poll event names for this meeting
  const [existingPolls, setExistingPolls] = useState([]);
  React.useEffect(() => {
    const fetchPolls = async () => {
      try {
        const response = await fetch(`${url}/polls/${meetingId}`);
        if (response.ok) {
          const data = await response.json();
          setExistingPolls(data.map(p => p.title));
        }
      } catch (e) {
        // ignore error
      }
    };
    if (showPollForm) fetchPolls();
  }, [showPollForm, meetingId, url]);

  // Only allow poll events not already created
  const pollEventOptions = agenda
    .filter(item =>
      item.agendaEvent?.eventType === "poll" &&
      !existingPolls.includes(item.agendaEvent.name)
    )
    .map(item => ({
      value: item.agendaEvent.name,
      label: item.agendaEvent.name
    }));

  const togglePollForm = () => setShowPollForm(!showPollForm);

  const handleSelectItem = (itemAgenda) => {
    const itemData = {
      agendaEventName: itemAgenda.agendaEvent.name,
      agendaItemId: itemAgenda.agendaItemId,
      assignedUserId: itemAgenda.assignedUser?.id || null,
      assignedUserFirstName: itemAgenda.assignedUser?.firstName || 'TBA',
      assignedUserLastName: itemAgenda.assignedUser?.lastName || '',
      meetingRoleName: itemAgenda.meetingRole?.name || 'N/A',
    };

    setSelectedItems((prevItems) =>
      prevItems.some((i) => i.agendaItemId === itemData.agendaItemId)
        ? prevItems.filter((i) => i.agendaItemId !== itemData.agendaItemId)
        : [...prevItems, itemData]
    );
  };

  const handleCreatePoll = async () => {
    if (!selectedPollEvent || selectedItems.length === 0) {
      toast.error('Please select a poll event and at least one agenda item.', {
        position: 'bottom-left',
      });
      return;
    }

    const newPoll = {
      title: selectedPollEvent.value,
      options: selectedItems.map((item) => ({
        firstName: item.assignedUserFirstName,
        lastName: item.assignedUserLastName,
        userId: item.assignedUserId,
        votes: 0, // Initialize votes for each option
      })),
      meeting: meetingId,
      status: 'ready',
    };

    try {
      const response = await fetch(`${url}/poll/${meetingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authUser.token,
        },
        body: JSON.stringify(newPoll),
      });

      const raw = await response.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(data?.Error || data?.error || response.statusText || 'Failed to create poll.');
      }

      if (data?.status === 'ready') {
        window.dispatchEvent(new CustomEvent('meeting:poll-created', {
          detail: {
            meetingId,
            pollId: data?._id || data?.id || null,
            title: newPoll.title,
          },
        }));
        getPolls(); // Fetch updated polls
        setSelectedPollEvent(null); // Reset poll event
        setSelectedItems([]); // Reset selected items
        setShowPollForm(false); // Hide the form
      } else {
        toast.error('Poll was created with an unexpected response.', { position: 'bottom-left' });
      }
    } catch (error) {
      console.error('Error creating poll:', error);
      toast.error(error.message || 'An error occurred while creating the poll.', { position: 'bottom-left' });
    }
  };

  // Custom styles for react-select
  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#343a40',
      color: '#fff',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#343a40',
      color: '#fff',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#fff',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#495057' : state.isFocused ? '#495057' : '#343a40',
      color: '#fff',
    }),
    input: (provided) => ({
      ...provided,
      color: '#fff',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
    }),
  };

  return (
    <>
      <Button
        variant="success"
        onClick={togglePollForm}
        className="mb-3 w-100"
      >
        {showPollForm ? 'Cancel Poll Creation' : 'Create Poll'}
      </Button>

      {showPollForm && (
        <Form data-bs-theme={'dark'}>
          <Form.Group controlId="pollEvent" className="mb-3">
            <Form.Label>Poll Event</Form.Label>
            {pollEventOptions.length === 0 ? (
              <Row>
                <Col className="text-center">
                  <Button
                    variant="danger"
                    onClick={togglePollForm}
                    className="mb-3"
                  >
                    All Poll Events for this meeting already exist.
                  </Button>
                </Col>
              </Row>
            ) : (
              <Select
                options={pollEventOptions}
                value={selectedPollEvent}
                onChange={setSelectedPollEvent}
                styles={customStyles}
                placeholder="Select poll event"
                menuPlacement="top"
                isClearable
                isSearchable
              />
            )}
          </Form.Group>
          {pollEventOptions.length > 0 && (
            <>
              <Form.Group controlId="agendaItems" className="mb-3">
                <Form.Label>Select Agenda Items</Form.Label>
                <ListGroup>
                  {agenda.map((item) => (
                    <ListGroup.Item
                      key={item.agendaItemId}
                      active={selectedItems.some((i) => i.agendaItemId === item.agendaItemId)}
                      onClick={() => handleSelectItem(item)}
                    >
                      {item.agendaEvent.name} - {item.assignedUser?.firstName || 'TBA'}{' '}
                      {item.assignedUser?.lastName || ''}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Form.Group>
              <Button
                variant="success"
                onClick={handleCreatePoll}
                className="w-100"
              >
                Create Poll
              </Button>
            </>
          )}
        </Form>
      )}
    </>
  );
};

export default AddPoll;
